/** XeAgents.ai**/
import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Loader2, Sparkles } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import * as VisuallyHidden from "@radix-ui/react-visually-hidden";
import { AuthStorage } from "@/lib/auth-storage";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { isIndianPlan } from "@shared/india-billing";
import { withBase } from "@/lib/base-path";

interface Plan {
  id: string;
  name: string;
  displayName: string;
  description: string;
  monthlyPrice: string;
  razorpayMonthlyPrice?: string | null;
  includedCredits: number;
  maxAgents: number;
  maxCampaigns: number;
  features?: Record<string, boolean>;
  isActive?: boolean;
  country?: string;
  stripeMonthlyPriceId?: string | null;
  razorpayPlanId?: string | null;
  paypalMonthlyPlanId?: string | null;
}

interface PaymentGatewayConfig {
  stripeEnabled: boolean;
  razorpayEnabled: boolean;
  paypalEnabled: boolean;
  paystackEnabled: boolean;
  mercadopagoEnabled: boolean;
  razorpayKeyId?: string;
  stripeCurrency?: string;
}

interface RazorpayConfig {
  enabled: boolean;
  keyId: string | null;
}

declare global {
  interface Window {
    Razorpay: any;
  }
}

interface PlanSelectionModalProps {
  isOpen: boolean;
  onPlanSelected: (planId: string) => void;
  onClose?: () => void; // Optional close handler for dismissible mode
  mode?: 'required' | 'welcome'; // 'required' = must select, 'welcome' = can dismiss
  title?: string;
  description?: string;
}

export function PlanSelectionModal({
  isOpen,
  onPlanSelected,
  onClose,
  mode = 'required',
  title,
  description
}: PlanSelectionModalProps) {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);
  const [razorpayLoaded, setRazorpayLoaded] = useState(false);
  const { toast } = useToast();

  const { data: paymentGateway } = useQuery<PaymentGatewayConfig>({
    queryKey: ["/api/settings/payment-gateway"],
  });

  const { data: razorpayConfig } = useQuery<RazorpayConfig>({
    queryKey: ["/api/razorpay/config"],
  });

  const { data: user } = useQuery<any>({
    queryKey: ["/api/auth/me"],
  });

  // Determine user's country for plan filtering
  // Priority: Organization country > User billing country > Default 'US'
  // Returns null if user data hasn't loaded yet to prevent premature defaults
  const normalizedCountry = user ? (() => {
    // First check organization country (most reliable for billing/plan context)
    const orgCountry = user?.organizationCountry?.toUpperCase();
    if (orgCountry === 'IN' || orgCountry === 'INDIA') return 'IN';
    if (orgCountry === 'US' || orgCountry === 'USA') return 'US';
    if (orgCountry) return orgCountry;
    
    // Fallback to user's billing country
    const userCountry = user?.billingCountry?.toUpperCase();
    if (userCountry === 'IN' || userCountry === 'INDIA') return 'IN';
    if (userCountry === 'US' || userCountry === 'USA') return 'US';
    if (userCountry) return userCountry;
    
    // Default to US
    return 'US';
  })() : null;

  const currencySymbol = normalizedCountry === 'IN' ? '₹' : '$';

  useEffect(() => {
    if (isOpen && user && normalizedCountry) {
      fetchPlans();
    }
  }, [isOpen, normalizedCountry]);

  // Load Razorpay script if enabled
  useEffect(() => {
    if (!paymentGateway?.razorpayEnabled) {
      return;
    }
    
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    script.onload = () => setRazorpayLoaded(true);
    document.body.appendChild(script);
    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, [paymentGateway?.razorpayEnabled]);

  const fetchPlans = async () => {
    if (!normalizedCountry) return;
    try {
      // Pass country parameter to server for proper filtering
      const response = await fetch(`/api/plans?country=${normalizedCountry}&app=xevoicepro`);
      if (!response.ok) throw new Error("Failed to fetch plans");
      const data = await response.json();
      // Show only active paid plans (server already filtered by country)
      // Filter out free plan (case-insensitive check)
      const activePlans = data.filter((p: Plan) => {
        const isActive = p.isActive === true;
        // A plan is free if name is "free" OR both USD and INR prices are 0
        const monthlyPrice = parseFloat(p.monthlyPrice || '0');
        const razorpayPrice = parseFloat(p.razorpayMonthlyPrice || '0');
        const isFree = p.name?.toLowerCase() === 'free' || (monthlyPrice === 0 && razorpayPrice === 0);
        return isActive && !isFree;
      });
      setPlans(activePlans);
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to load plans",
        variant: "destructive",
      });
    }
  };

  // Payment gateway mutations
  const stripeCheckout = useMutation<any, Error, { planId: string; billingPeriod: string }>({
    mutationFn: async ({ planId, billingPeriod }) => {
      const response = await apiRequest("POST", "/api/stripe/create-checkout-session", {
        type: "subscription",
        planId,
        billingPeriod,
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to create checkout session");
      }
      return response.json();
    },
    onSuccess: (data) => {
      if (data.url) {
        window.location.href = data.url;
      }
    },
    onError: (error) => {
      toast({
        title: "Checkout Failed",
        description: error.message,
        variant: "destructive",
      });
      setLoading(false);
      setSelectedPlanId(null);
    },
  });

  const razorpaySubscription = useMutation<any, Error, { planId: string; billingPeriod: string }>({
    mutationFn: async ({ planId, billingPeriod }) => {
      const response = await apiRequest("POST", "/api/razorpay/create-subscription", {
        planId,
        billingPeriod,
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to create subscription");
      }
      return response.json();
    },
    onSuccess: (data) => {
      if (!window.Razorpay || !razorpayLoaded) {
        toast({
          title: "Razorpay Not Loaded",
          description: "Please wait a moment and try again",
          variant: "destructive",
        });
        setLoading(false);
        setSelectedPlanId(null);
        return;
      }

      const selectedPlan = plans.find(p => p.id === selectedPlanId);
      
      const options = {
        key: razorpayConfig?.keyId || paymentGateway?.razorpayKeyId,
        subscription_id: data.subscriptionId,
        name: "XeAgents",
        description: `${selectedPlan?.displayName} - Monthly`,
        handler: async function (response: any) {
          try {
            const verifyResponse = await apiRequest("POST", "/api/razorpay/verify-subscription", {
              razorpay_subscription_id: response.razorpay_subscription_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });

            if (verifyResponse.ok) {
              toast({
                title: "Subscription Active",
                description: "Your subscription has been activated successfully!",
              });
              window.location.href = withBase("/app?success=true");
            } else {
              const error = await verifyResponse.json();
              throw new Error(error.error || "Verification failed");
            }
          } catch (error: any) {
            toast({
              title: "Verification Failed",
              description: error.message,
              variant: "destructive",
            });
          }
          setLoading(false);
          setSelectedPlanId(null);
        },
        modal: {
          ondismiss: function () {
            setLoading(false);
            setSelectedPlanId(null);
          },
        },
        prefill: {
          email: user?.email,
          name: user?.name,
        },
        theme: {
          color: "#6366f1",
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    },
    onError: (error) => {
      toast({
        title: "Subscription Failed",
        description: error.message,
        variant: "destructive",
      });
      setLoading(false);
      setSelectedPlanId(null);
    },
  });

  const paypalSubscription = useMutation<any, Error, { planId: string; billingPeriod: string }>({
    mutationFn: async ({ planId, billingPeriod }) => {
      const response = await apiRequest("POST", "/api/paypal/create-subscription", { planId, billingPeriod });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to create PayPal subscription");
      }
      return response.json();
    },
    onSuccess: (data) => {
      if (data.approvalUrl) {
        window.location.href = data.approvalUrl;
      }
    },
    onError: (error) => {
      toast({ title: "Subscription Failed", description: error.message, variant: "destructive" });
      setLoading(false);
      setSelectedPlanId(null);
    },
  });

  const paystackSubscription = useMutation<any, Error, { planId: string; billingPeriod: string }>({
    mutationFn: async ({ planId, billingPeriod }) => {
      const response = await apiRequest("POST", "/api/paystack/create-subscription", { planId, billingPeriod });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to create Paystack subscription");
      }
      return response.json();
    },
    onSuccess: (data) => {
      if (data.authorizationUrl) {
        window.location.href = data.authorizationUrl;
      }
    },
    onError: (error) => {
      toast({ title: "Subscription Failed", description: error.message, variant: "destructive" });
      setLoading(false);
      setSelectedPlanId(null);
    },
  });

  const handleSelectPlan = async (planId: string) => {
    // Prevent multiple simultaneous clicks
    if (loading) {
      return;
    }
    
    setLoading(true);
    setSelectedPlanId(planId);
    
    try {
      // Find the selected plan to check if it's free or paid
      const selectedPlan = plans.find(p => p.id === planId);
      
      if (!selectedPlan) {
        throw new Error("Selected plan not found");
      }
      
      // Validate plan is active
      if (selectedPlan.isActive === false) {
        throw new Error("This plan is no longer available. Please select another plan.");
      }
      
      // All plans in this modal are paid plans (free plan is auto-assigned during registration)

      // India pays through Razorpay. An Indian plan is never sent to Stripe, and if Razorpay is not ready
      // the customer is told so rather than being charged through the wrong account.
      if (isIndianPlan(selectedPlan)) {
        if (!paymentGateway?.razorpayEnabled) {
          throw new Error("Online payment for India is not available yet. Please contact support.");
        }
        if (!selectedPlan.razorpayPlanId) {
          throw new Error("Payment is not set up for this plan yet. Please contact support.");
        }
        toast({
          title: "Opening Payment",
          description: "Starting secure Razorpay checkout...",
        });
        // Razorpay opens its own overlay; the mutation's handlers manage the loading state from here.
        razorpaySubscription.mutate({ planId, billingPeriod: "monthly" });
        return;
      }

      // Every other plan: Stripe checkout

      // Check if the plan has any payment gateway configured
      // Stripe can work without pre-created price IDs (server creates inline price_data)
      // For Indian plans, razorpayMonthlyPrice may hold the INR price while monthlyPrice could also have a value
      const hasMonthlyPrice = parseFloat(selectedPlan.monthlyPrice || '0') > 0;
      const hasRazorpayPrice = parseFloat(selectedPlan.razorpayMonthlyPrice || '0') > 0;
      const hasStripePrice = !!selectedPlan.stripeMonthlyPriceId || hasMonthlyPrice || hasRazorpayPrice;
      if (!hasStripePrice && !selectedPlan.razorpayPlanId && !selectedPlan.paypalMonthlyPlanId) {
        throw new Error("Payment gateway not configured for this plan. Please contact support.");
      }

      // Non-Indian paid plans use Stripe
      if (!hasStripePrice) {
        throw new Error("Stripe pricing not configured for this plan. Please contact support.");
      }

      toast({
        title: "Redirecting to Payment",
        description: "Initiating secure Stripe checkout...",
      });

      // For paid plans, do NOT call onPlanSelected here because callers 
      // (e.g. App.tsx UserGuard) may trigger window.location.reload() which 
      // would kill the Stripe checkout redirect before it completes.
      // The stripeCheckout.onSuccess handler will redirect to Stripe's checkout URL.
      // Do NOT reset loading state in finally block - let the mutation handlers manage it.
      stripeCheckout.mutate({ planId, billingPeriod: "monthly" });
      
      // Return early - don't hit the finally block which resets loading state
      return;
    } catch (error: any) {
      // Handle specific error types
      if (error.name === 'AbortError') {
        toast({
          title: "Request Timeout",
          description: "The request took too long. Please check your connection and try again.",
          variant: "destructive",
        });
      } else if (error.message?.includes("token") || error.message?.includes("Authentication")) {
        toast({
          title: "Session Expired",
          description: "Your session has expired. Please login again.",
          variant: "destructive",
        });
        // Clear auth and redirect to login
        AuthStorage.removeToken();
        window.location.href = withBase("/login");
      } else {
        toast({
          title: "Error",
          description: error.message || "Failed to select plan",
          variant: "destructive",
        });
      }
    } finally {
      setLoading(false);
      setSelectedPlanId(null);
    }
  };

  const isDismissible = mode === 'welcome' && onClose;

  return (
    <Dialog open={isOpen} onOpenChange={isDismissible ? onClose : () => {}} modal={true}>
      <DialogContent
        hideCloseButton={!isDismissible}
        className="max-w-5xl p-0 gap-0 overflow-hidden"
        onPointerDownOutside={(e) => !isDismissible && e.preventDefault()}
        onEscapeKeyDown={(e) => !isDismissible && e.preventDefault()}
      >
        <VisuallyHidden.Root>
          <DialogTitle>{title || "Choose Your Plan"}</DialogTitle>
          <DialogDescription>{description || "Select a plan to get started with your account"}</DialogDescription>
        </VisuallyHidden.Root>

        <div className="p-8">
          <div className="text-center mb-8">
            <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full mb-4 ${
              mode === 'welcome'
                ? "bg-gradient-to-br from-green-500 to-emerald-600"
                : "bg-gradient-to-br from-amber-500 to-orange-600"
            }`}>
              <Sparkles className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-3xl font-bold mb-2">
              {mode === 'welcome'
                ? (title || "Welcome to Your Enterprise Account! 🎉")
                : "Your Credits Have Run Out"}
            </h2>
            <p className="text-muted-foreground">
              {mode === 'welcome'
                ? (description || "Here are the available plans you can explore. Your enterprise account is already active!")
                : "Purchase more credits to continue using the platform and unlock additional features"}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {plans.map((plan) => {
              const isPopular = plan.name === 'growth';
              
              // Determine the display price based on country
              // For Indian plans, prioritize razorpayMonthlyPrice since monthlyPrice is often 0
              const displayPrice = `${currencySymbol}${
                normalizedCountry === 'IN' && plan.razorpayMonthlyPrice && parseFloat(plan.razorpayMonthlyPrice) > 0
                  ? plan.razorpayMonthlyPrice
                  : plan.monthlyPrice || '0'
              }`;

              return (
                <div
                  key={plan.id}
                  className={`relative rounded-xl border-2 p-6 transition-all ${
                    isPopular
                      ? "border-amber-500 shadow-lg shadow-amber-500/20"
                      : "border-border hover:border-primary/50"
                  }`}
                >
                  {isPopular && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-gradient-to-r from-amber-500 to-orange-600 text-white text-xs font-semibold rounded-full">
                      MOST POPULAR
                    </div>
                  )}

                  <div className="mb-4">
                    <h3 className="text-xl font-bold mb-2">{plan.displayName}</h3>
                    <p className="text-sm text-muted-foreground mb-4 min-h-[60px]">
                      {plan.description}
                    </p>
                    <div className="flex items-baseline gap-1">
                      <span className="text-3xl font-bold">
                        {displayPrice}
                      </span>
                      <span className="text-muted-foreground">/month</span>
                    </div>
                  </div>

                  <div className="space-y-3 mb-6">
                    <div className="flex items-center gap-2 text-sm">
                      <CheckCircle2 className="w-4 h-4 text-green-600" />
                      <span>{plan.includedCredits} credits</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <CheckCircle2 className="w-4 h-4 text-green-600" />
                      <span>Up to {plan.maxAgents} AI agents</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <CheckCircle2 className="w-4 h-4 text-green-600" />
                      <span>{plan.maxCampaigns} campaigns</span>
                    </div>
                  </div>

                  <Button
                    onClick={() => handleSelectPlan(plan.id)}
                    disabled={loading}
                    className={`w-full ${
                      isPopular
                        ? "bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white"
                        : ""
                    }`}
                  >
                    {loading && selectedPlanId === plan.id ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      "Upgrade Now"
                    )}
                  </Button>
                </div>
              );
            })}
          </div>

          <div className="mt-6 text-center text-sm text-muted-foreground">
            <p>You can upgrade or downgrade your plan anytime from your dashboard</p>
          </div>

          {/* Continue button for welcome mode */}
          {mode === 'welcome' && onClose && (
            <div className="mt-6 text-center">
              <Button
                variant="outline"
                size="lg"
                onClick={onClose}
                className="min-w-[200px]"
              >
                Continue to Dashboard →
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}